import * as React from 'react';
import {
    DeviceIntelligenceContext,
    FingerPrintResponseData,
    BureauResponseData,
    MangopayResponseData,
    IntelSDKStatus,
    IncogniaResponseData,
    DeviceIntelligenceHeaderConfig,
    DeviceIntelligenceIds,
    FingerprintVendorTokens,
    ForterResponseData,
} from '../DeviceIntelligenceContext';
import { loadBureauSdk } from '../loadBureauSdk';
import { loadMangopaySdk } from '../mangopay/loadMangopaySdk';
import * as FingerprintJS from '@fingerprintjs/fingerprintjs-pro';
import { v4 as uuidV4 } from 'uuid';
import { useAppContext } from '@contexts/app/hooks/useAppContext';
import { useExperiment } from '@contexts/experiment/useExperiment';
import { universalLoginExperiment } from '@experiments/universalLogin';
import { MessagingClient } from '@services/messagingClient/MessagingClient';
import { ADPMeasurement } from '@services/measurement';
import { ADPLogger } from '@services/logger/ADPLogger';
import { ConsoleLogger } from '@services/logger/ConsoleLogger';
import { useVisitorData } from '@fingerprintjs/fingerprintjs-pro-react';
import IncogniaWebSdk from '@incognia/web-sdk';
import { DeviceIntelligenceHeader } from '@apis/model/request/Header';
import { useServiceResolver } from '@services/useServiceResolver';
import { loadForterSdk, registerForterTokenListener } from '../forter/loadForterSdk';

export function WithDeviceIntelligence<TProps>(Component: React.FC<TProps>): React.FC<React.PropsWithRef<TProps>> {
    const MemoizedComponent = React.memo<React.FC<TProps>>(Component);

    const ComponentWithDeviceIntelligence: React.FC<React.PropsWithRef<TProps>> = props => {
        const bureauSessionId = uuidV4();
        const mangopayAttemptReference = uuidV4();
        const appContext = useAppContext();

        const { getData } = useVisitorData({ ignoreCache: true }, { immediate: false });

        const messagingClient = MessagingClient.instance;
        const measurement = new ADPMeasurement(messagingClient, appContext);
        const logger = __QA__ || __PROD__ ? new ADPLogger(messagingClient, appContext) : ConsoleLogger;

        const experiments = useExperiment(universalLoginExperiment);
        let deviceIntelligenceHeader: Record<string, string> | null = null;
        let deviceIntelligenceIds: DeviceIntelligenceIds = {
            mangopay: '',
            fingerprint: { requestId: '', visitorId: '' },
            bureau: '',
            incognia: '',
            forter: '',
        };
        let isDeviceIntelligenceExecuted = false;
        const initializeBureau = async (eventId: string = bureauSessionId, userId: string = appContext.userId) => {
            const bureau = await loadBureauSdk();
            await bureau.init({
                eventId,
                userId,
                credentialId: 'eb4d655a-2191-478d-be95-9f6d76ce90a6',
                environment: 'PRODUCTION',
                flow: 'Login',
            });
        };

        const intitalizeMangopay = async (
            attemptReference: string = mangopayAttemptReference,
            sensitiveFields: string[] = ['ccn', 'cvv'],
            secretFields: string[] = ['password']
        ) => {
            const mangopay = await loadMangopaySdk();
            await mangopay.init({
                attemptReference,
                sensitiveFields,
                secretFields,
            });
        };

        const submitMangoPayDataWithTimeout = async (timeout: number = 1500): Promise<MangopayResponseData> => {
            await loadMangopaySdk();
            return new Promise(resolve => {
                const timer = setTimeout(() => {
                    logger.error('Mangopay profiling timed out');
                    resolve({ status: IntelSDKStatus.Timeout, attemptReference: mangopayAttemptReference });
                }, timeout);

                if (window.dftp) {
                    window.dftp
                        .profileCompleted()
                        .then(() => {
                            clearTimeout(timer);
                            resolve({
                                status: IntelSDKStatus.Success,
                                attemptReference: mangopayAttemptReference,
                            });
                        })
                        .catch(err => {
                            clearTimeout(timer);
                            logger.error('Mangopay profiling failed with error', err);
                            resolve({ status: IntelSDKStatus.Error, attemptReference: mangopayAttemptReference });
                        });
                } else {
                    clearTimeout(timer);
                    logger.error('dftp not available for Mangopay.');
                    resolve({ status: IntelSDKStatus.Error, attemptReference: null });
                }
            });
        };

        const sumbitMangopayData = async (timeout?: number) => {
            return await measurement.measurePromise(
                'ul.clientside.vendor',
                submitMangoPayDataWithTimeout(timeout),
                response => ({
                    status: response.status,
                    vendor: 'mangopay',
                    MangopayAttemptReferenceID: response.attemptReference,
                })
            );
        };

        const submitBureauDataWithTimeout = async (timeout: number = 1500): Promise<BureauResponseData> => {
            const bureau = await loadBureauSdk();
            return new Promise(resolve => {
                const timer = setTimeout(() => {
                    logger.error('Bureau timeout');
                    resolve({ status: IntelSDKStatus.Timeout });
                }, timeout);

                bureau.onSubmit(response => {
                    clearTimeout(timer);
                    if (response?.status === 200) {
                        resolve({ status: IntelSDKStatus.Success, sessionId: bureauSessionId });
                    } else {
                        logger.error('Bureau submit error', { httpResponseCode: response.status });
                        resolve({ status: IntelSDKStatus.Error });
                    }
                });
            });
        };

        const submitBureauData = async (timeout?: number) => {
            return await measurement.measurePromise(
                'ul.clientside.vendor',
                submitBureauDataWithTimeout(timeout),
                response => ({
                    status: response.status,
                    vendor: 'bureau',
                })
            );
        };

        const submitAndGetFingerprintIds = async (timeout?: number) => {
            return await measurement.measurePromise(
                'ul.clientside.vendor',
                getFingerPrintIdsWithTimeout(timeout),
                response => ({
                    status: response.status,
                    vendor: 'fingerprintjs',
                })
            );
        };

        const initializeIncognia = async () => {
            IncogniaWebSdk.init({ appId: 'cda21232-7aa6-4430-a5b5-c7e0f6e8172c' });
        };

        const submitAndGetIncogniaIds = async (timeout?: number) => {
            return await measurement.measurePromise('ul.clientside.vendor', getIncogniaIds(), response => ({
                status: response.status,
                vendor: 'incognia',
            }));
        };

        const getFingerPrintIdsWithTimeout = async (timeout: number = 1500): Promise<FingerPrintResponseData> => {
            try {
                const { visitorId, requestId } = await getData({ timeout });
                return { visitorId, requestId, status: IntelSDKStatus.Success };
            } catch (error) {
                switch (error.message) {
                    case FingerprintJS.ERROR_CLIENT_TIMEOUT:
                        logger.error('fingerprint timeout');
                        return { status: IntelSDKStatus.Timeout };
                    default:
                        logger.error('FingerprintJS submit error', error.message);
                        return { status: IntelSDKStatus.Error };
                }
            }
        };

        const getIncogniaIds = async (): Promise<IncogniaResponseData> => {
            await initializeIncognia();
            try {
                IncogniaWebSdk.setAccountId(appContext.userId);
                const requestToken = await IncogniaWebSdk.generateRequestToken({ askForGeolocation: false });
                return { requestToken, status: IntelSDKStatus.Success };
            } catch (error) {
                logger.error('incognia submit error', error.message);
                return { status: IntelSDKStatus.Error };
            }
        };

        const getForterToken = async (): Promise<ForterResponseData> => {
            try {
                // Check if Forter is enabled via experiments
                if (
                    !(
                        experiments.DEVICE_INTELLIGENCE_PARENT_EXPERIMENT &&
                        experiments.USE_FORTER_FOR_DEVICE_INTELLIGENCE
                    )
                ) {
                    return { status: IntelSDKStatus.Error };
                }

                // Try to get token from cookie
                const cookieToken = document.cookie.split(';').find(row => row.trim().startsWith('forterToken='));

                if (cookieToken) {
                    const token = cookieToken.split('=')[1];

                    // Check if token is non-empty
                    if (!token || token.trim() === '') {
                        logger.warning('Forter token is present but empty.');
                        return { status: IntelSDKStatus.Error };
                    }

                    return { token, status: IntelSDKStatus.Success };
                }

                logger.error('Forter token not available');
                return { status: IntelSDKStatus.Error };
            } catch (error) {
                logger.error('Error getting Forter token', error?.message || String(error));
                return { status: IntelSDKStatus.Error };
            }
        };

        const submitForterData = async () => {
            return await measurement.measurePromise('ul.clientside.vendor', getForterToken(), response => ({
                status: response.status,
                vendor: 'forter',
            }));
        };

        const buildForterHeader = async () => {
            if (deviceIntelligenceIds?.forter) {
                logger.info('Returning existing Forter token', {
                    token: deviceIntelligenceIds.forter,
                });
                return {
                    [DeviceIntelligenceHeader.ForterToken]: deviceIntelligenceIds.forter,
                };
            }

            const result = await submitForterData();

            if (result.status === IntelSDKStatus.Success) {
                deviceIntelligenceIds = { ...deviceIntelligenceIds, forter: result.token };
                logger.info('Forter token generated successfully');
            }

            return result.status === IntelSDKStatus.Success
                ? {
                      [DeviceIntelligenceHeader.ForterToken]: result.token,
                  }
                : {};
        };

        const buildFingerprintHeader = async () => {
            if (
                deviceIntelligenceIds.fingerprint &&
                deviceIntelligenceIds.fingerprint.requestId &&
                deviceIntelligenceIds.fingerprint.visitorId
            ) {
                logger.info('Returning existing Fingerprint header', {
                    FingerprintRequestId: deviceIntelligenceIds.fingerprint.requestId,
                    FingerprintVisitorId: deviceIntelligenceIds.fingerprint.visitorId,
                });
                return {
                    [DeviceIntelligenceHeader.FingerprintRequestId]: deviceIntelligenceIds.fingerprint.requestId,
                    [DeviceIntelligenceHeader.FingerprintVisitorId]: deviceIntelligenceIds.fingerprint.visitorId,
                };
            }
            const result = await submitAndGetFingerprintIds();
            if (result.status === IntelSDKStatus.Success) {
                deviceIntelligenceIds = {
                    ...deviceIntelligenceIds,
                    fingerprint: { visitorId: result.visitorId, requestId: result.requestId },
                };
            }
            return result.status === IntelSDKStatus.Success
                ? {
                      [DeviceIntelligenceHeader.FingerprintRequestId]: result.requestId,
                      [DeviceIntelligenceHeader.FingerprintVisitorId]: result.visitorId,
                  }
                : {};
        };

        const buildIncogniaHeader = async () => {
            if (deviceIntelligenceIds?.incognia) {
                logger.info('Returning existing Incognia header', {
                    IncogniaRequestToken: deviceIntelligenceIds.incognia,
                });
                return {
                    [DeviceIntelligenceHeader.IncogniaRequestToken]: deviceIntelligenceIds.incognia,
                };
            }
            const result = await submitAndGetIncogniaIds();
            if (result.status === IntelSDKStatus.Success) {
                deviceIntelligenceIds = { ...deviceIntelligenceIds, incognia: result.requestToken };
            }
            return result.status === IntelSDKStatus.Success
                ? {
                      [DeviceIntelligenceHeader.IncogniaRequestToken]: result.requestToken,
                  }
                : {};
        };

        const buildBureauHeader = async () => {
            if (deviceIntelligenceIds?.bureau) {
                logger.info('Returning existing Bureau header', {
                    BureauSessionId: deviceIntelligenceIds.bureau,
                });
                return {
                    [DeviceIntelligenceHeader.BureauSessionId]: deviceIntelligenceIds.bureau,
                };
            }
            const result = await submitBureauData();
            if (result.status === IntelSDKStatus.Success) {
                deviceIntelligenceIds = { ...deviceIntelligenceIds, bureau: result.sessionId };
            }
            return result.status === IntelSDKStatus.Success
                ? {
                      [DeviceIntelligenceHeader.BureauSessionId]: result.sessionId,
                  }
                : {};
        };

        const buildMangopayHeader = async () => {
            const currentAttemptReference = mangopayAttemptReference;

            if (deviceIntelligenceIds?.mangopay === currentAttemptReference) {
                logger.info('Mangopay header for attemptReference already constructed or processing initiated', {
                    MangopayAttemptReferenceID: currentAttemptReference,
                });
            } else {
                deviceIntelligenceIds = { ...deviceIntelligenceIds, mangopay: currentAttemptReference };

                sumbitMangopayData()
                    .then(result => {
                        if (result.status !== IntelSDKStatus.Success) {
                            logger.warning('Mangopay profiling for header ID completed with non-success status:', {
                                status: result.status,
                                attemptReference: currentAttemptReference,
                            });
                        } else {
                            logger.info('Mangopay profiling for header ID successful:', {
                                status: result.status,
                                attemptReference: currentAttemptReference,
                            });
                        }
                    })
                    .catch(error => {
                        logger.error('Error submitting Mangopay data in background for header ID:', {
                            error: error.message,
                            attemptReference: currentAttemptReference,
                        });
                    });
            }

            logger.info('Preemptively building Mangopay header with ID', {
                MangopayAttemptReferenceID: currentAttemptReference,
            });
            return {
                [DeviceIntelligenceHeader.MangopayAttemptReferenceID]: currentAttemptReference,
            };
        };

        const buildDeviceIntelligenceHeader = async (config: DeviceIntelligenceHeaderConfig = {}) => {
            const fingerprintPromise = config.skipFingerprint ? Promise.resolve({}) : buildFingerprintHeader();
            const incogniaPromise = config.skipIncognia ? Promise.resolve({}) : buildIncogniaHeader();
            const mangopayPromise = config.skipMangopay ? Promise.resolve({}) : buildMangopayHeader();
            const bureauPromise = config.skipBureau ? Promise.resolve({}) : buildBureauHeader();
            const forterPromise = config.skipForter ? Promise.resolve({}) : buildForterHeader();

            const results = await Promise.allSettled([
                fingerprintPromise,
                incogniaPromise,
                mangopayPromise,
                bureauPromise,
                forterPromise,
            ]);

            const headers: Record<string, string> = {};

            results.forEach((result, index) => {
                if (result.status === 'fulfilled') {
                    Object.assign(headers, result.value);
                }
            });
            return headers;
        };

        const buildDeviceIntelligenceHeaderAsync = async (): Promise<void> => {
            const parentExperimentEnabled = experiments.DEVICE_INTELLIGENCE_PARENT_EXPERIMENT;
            const header = await buildDeviceIntelligenceHeader({
                skipFingerprint: !(parentExperimentEnabled && experiments.USE_DEVICE_INTELLIGENCE_FINGERPRINT_B2C),
                skipBureau: !(parentExperimentEnabled && experiments.USE_DEVICE_INTELLIGENCE_BUREAU_B2C),
                skipMangopay: !(parentExperimentEnabled && experiments.USE_MANGO_PAY_FOR_DEVICE_INTELLIGENCE),
                skipIncognia: !(parentExperimentEnabled && experiments.USE_INCOGNIA_FOR_DEVICE_INTELLIGENCE),
                skipForter: !(parentExperimentEnabled && experiments.USE_FORTER_FOR_DEVICE_INTELLIGENCE),
            });
            deviceIntelligenceHeader = header;
            isDeviceIntelligenceExecuted = true;
        };

        const isDeviceIntelligenceEnabled = () => {
            return (
                experiments.USE_DEVICE_INTELLIGENCE ||
                experiments.DEVICE_INTELLIGENCE_PARENT_EXPERIMENT ||
                experiments.USE_DEVICE_INTELLIGENCE_FINGERPRINT_B2C ||
                experiments.USE_DEVICE_INTELLIGENCE_BUREAU_B2C ||
                experiments.USE_MANGO_PAY_FOR_DEVICE_INTELLIGENCE ||
                experiments.USE_INCOGNIA_FOR_DEVICE_INTELLIGENCE ||
                experiments.USE_FORTER_FOR_DEVICE_INTELLIGENCE
            );
        };

        const getDeviceIntelligenceHeaders = () => {
            return deviceIntelligenceHeader;
        };

        const isDeviceIntelligenceHeaderBuilt = () => {
            return isDeviceIntelligenceExecuted;
        };

        React.useEffect(() => {
            if (experiments.DEVICE_INTELLIGENCE_PARENT_EXPERIMENT && experiments.USE_DEVICE_INTELLIGENCE_BUREAU_B2C) {
                initializeBureau();
            }
        }, []);

        React.useEffect(() => {
            if (
                experiments.DEVICE_INTELLIGENCE_PARENT_EXPERIMENT &&
                experiments.USE_MANGO_PAY_FOR_DEVICE_INTELLIGENCE
            ) {
                intitalizeMangopay();
            }
        }, []);

        React.useEffect(() => {
            if (experiments.DEVICE_INTELLIGENCE_PARENT_EXPERIMENT && experiments.USE_INCOGNIA_FOR_DEVICE_INTELLIGENCE) {
                initializeIncognia();
            }
        }, []);

        return (
            <DeviceIntelligenceContext.Provider
                value={{
                    buildDeviceIntelligenceHeader,
                    buildDeviceIntelligenceHeaderAsync,
                    isDeviceIntelligenceEnabled,
                    isDeviceIntelligenceHeaderBuilt,
                    getDeviceIntelligenceHeaders,
                }}
            >
                <MemoizedComponent {...props} />
            </DeviceIntelligenceContext.Provider>
        );
    };

    ComponentWithDeviceIntelligence.displayName = `${Component.displayName || 'Component'}WithDeviceIntelligence`;

    return ComponentWithDeviceIntelligence;
}
